import { makeStyles } from '@material-ui/core';
import { memo } from 'react';
import { COLORS } from '../../../theme/variables';
import { SCORE_GRADIENT } from './utils';

type ViewType = 'venture-mentor' | 'mentor-venture';

interface HeatmapSidebarProps {
  viewType: ViewType;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 190,
    flexShrink: 0,
    borderLeft: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    marginBottom: 8,
  },
  gradientBar: {
    height: 12,
    borderRadius: 6,
    width: '100%',
  },
  gradientLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
    fontSize: 10,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: COLORS.COLOR_TEXT_LIGHTENED_10,
    marginBottom: 6,
  },
  legendSquare: {
    width: 16,
    height: 16,
    borderRadius: 3,
    flexShrink: 0,
  },
  instructions: {
    fontSize: 12,
    lineHeight: 1.5,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
  },
}));

const HeatmapSidebar = memo(function HeatmapSidebar({
  viewType,
}: HeatmapSidebarProps) {
  const classes = useStyles();

  const targetText =
    viewType === 'venture-mentor'
      ? 'another venture card'
      : 'another mentor card';

  return (
    <div className={classes.root} data-testid='heatmap-sidebar'>
      <div>
        <div className={classes.sectionTitle}>Score Scale</div>
        <div
          className={classes.gradientBar}
          style={{ background: SCORE_GRADIENT }}
        />
        <div className={classes.gradientLabels}>
          <span>Low</span>
          <span>High</span>
        </div>
      </div>

      <div>
        <div className={classes.sectionTitle}>Legend</div>
        <div className={classes.legendRow}>
          <div
            className={classes.legendSquare}
            style={{
              backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_40,
              border: `2px solid ${COLORS.COLOR_GRAY_LIGHTENED_20}`,
            }}
          />
          Candidate
        </div>
        {viewType === 'venture-mentor' && (
          <div className={classes.legendRow}>
            <div
              className={classes.legendSquare}
              style={{
                backgroundColor: COLORS.COLOR_GREEN_LIGHTENED_45,
                border: `2px solid ${COLORS.COLOR_GREEN_BASE}`,
                borderRadius: 3,
              }}
            />
            Fulfilled
          </div>
        )}
      </div>

      <div>
        <div className={classes.sectionTitle}>Interactions</div>
        <p className={classes.instructions}>
          Drag a chip to {targetText} to reassign. Click X on any chip to remove
          it from the bucket.
        </p>
      </div>
    </div>
  );
});

export default HeatmapSidebar;
